import { useMemo, useState } from "react";
import {
    Button,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";

import { ConfigField, IConfig } from "Pages/Setup/Connect/connectShared";
import {
    POSTING_FIELDS,
    REIMBURSEMENT_VENDOR_FIELDS,
} from "Pages/Setup/Connect/connectData";
import receiptSearchImage from "Pages/Setup/Connect/img/receipt_search.png";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

interface Props {
    find: (suffix: string) => IConfig | undefined;
    onEdit: (suffix: string, secret: boolean, index: number) => void;
    onSave: (suffix: string, value: string) => Promise<boolean>;
    step: number;
    onStepChange: (step: number) => void;
}

const STEPS = [
    "Allow Intercompany Expenses",
    "Enable Equipment Costing",
    "Posting defaults",
    "Key players",
    "Reimbursement vendors",
    "Receipt/invoice syncing",
];
const KEY_PLAYERS_SUFFIX = "integration.cmic.job_approver_codes";
const RECEIPT_FLYSHEET_NAME = "Ramp Attachment Link";
const ALLOW_RECEIPT_SYNC_SUFFIX = "integration.ramp.allow_attachment_sync";
const INTERCOMPANY_SUFFIX = "integration.use_custom_company_field";
const EQUIPMENT_COSTING_SUFFIX = "integration.use_equipment_costing";

function toBool(value: string | undefined): boolean {
    if (!value) return false;
    return ["true", "1", "yes", "y", "on"].includes(
        value.trim().toLowerCase()
    );
}

interface KeyPlayerRow {
    code: string;
    name: string;
}

export default function MapData({
    find,
    onEdit,
    onSave,
    step,
    onStepChange,
}: Props) {
    const intercompanyConfig = find(INTERCOMPANY_SUFFIX);
    const intercompanyConfigured = !!intercompanyConfig;
    const intercompanyEnabled = toBool(intercompanyConfig?.keyValue);
    const [savingIntercompany, setSavingIntercompany] = useState(false);
    const [intercompanyError, setIntercompanyError] = useState("");
    const equipmentCostingConfig = find(EQUIPMENT_COSTING_SUFFIX);
    const equipmentCostingConfigured = !!equipmentCostingConfig;
    const equipmentCostingEnabled = toBool(equipmentCostingConfig?.keyValue);
    const [savingEquipmentCosting, setSavingEquipmentCosting] = useState(false);
    const [equipmentCostingError, setEquipmentCostingError] = useState("");
    const [keyPlayersModalOpen, setKeyPlayersModalOpen] = useState(false);
    const [savingKeyPlayers, setSavingKeyPlayers] = useState(false);
    const [keyPlayersError, setKeyPlayersError] = useState("");
    const [copiedFlysheetName, setCopiedFlysheetName] = useState(false);
    const [savingReceiptSync, setSavingReceiptSync] = useState(false);
    const [keyPlayersRows, setKeyPlayersRows] = useState<KeyPlayerRow[]>([
        { code: "", name: "" },
    ]);

    const keyPlayersValue = find(KEY_PLAYERS_SUFFIX)?.keyValue?.trim() ?? "";
    const keyPlayersLocked = keyPlayersValue.length > 0;
    const allowReceiptSync = toBool(find(ALLOW_RECEIPT_SYNC_SUFFIX)?.keyValue);

    const canConfirmKeyPlayers = useMemo(() => {
        if (!keyPlayersRows.length) return false;
        let hasAny = false;
        for (const row of keyPlayersRows) {
            const code = row.code.trim();
            const name = row.name.trim();
            if (!code && !name) continue;
            if (!code || !name) return false;
            if (!/^[a-zA-Z]+$/.test(code)) return false;
            if (name.includes("|") || name.includes(".")) return false;
            hasAny = true;
        }
        return hasAny;
    }, [keyPlayersRows]);

    const openKeyPlayersModal = () => {
        setKeyPlayersError("");
        setKeyPlayersRows([{ code: "", name: "" }]);
        setKeyPlayersModalOpen(true);
    };

    const saveEquipmentCostingChoice = async (enabled: boolean) => {
        if (savingEquipmentCosting || equipmentCostingConfigured) return;

        setSavingEquipmentCosting(true);
        setEquipmentCostingError("");
        try {
            const saved = await onSave(
                EQUIPMENT_COSTING_SUFFIX,
                enabled ? "true" : "false"
            );
            if (!saved) {
                setEquipmentCostingError("Could not save the equipment costing choice. Please try again.");
            }
        } catch {
            setEquipmentCostingError("Could not save the equipment costing choice. Please try again.");
        } finally {
            setSavingEquipmentCosting(false);
        }
    };

    const saveIntercompanyChoice = async (enabled: boolean) => {
        if (savingIntercompany || intercompanyConfigured) return;

        setSavingIntercompany(true);
        setIntercompanyError("");
        try {
            const saved = await onSave(
                INTERCOMPANY_SUFFIX,
                enabled ? "true" : "false"
            );
            if (!saved) {
                setIntercompanyError("Could not save the intercompany choice. Please try again.");
            }
        } catch {
            setIntercompanyError("Could not save the intercompany choice. Please try again.");
        } finally {
            setSavingIntercompany(false);
        }
    };

    const compiledKeyPlayersPreview = useMemo(
        () =>
            keyPlayersRows
                .map((row) => ({
                    code: row.code.trim(),
                    name: row.name.trim(),
                }))
                .filter((row) => row.code && row.name)
                .map((row) => `${row.code}.${row.name}`)
                .join("|"),
        [keyPlayersRows]
    );

    const confirmKeyPlayers = async () => {
        if (!canConfirmKeyPlayers || savingKeyPlayers) {
            setKeyPlayersError("Enter at least one complete code and name pair.");
            return;
        }

        setSavingKeyPlayers(true);
        setKeyPlayersError("");
        const ok = await onSave(KEY_PLAYERS_SUFFIX, compiledKeyPlayersPreview);
        setSavingKeyPlayers(false);

        if (!ok) {
            setKeyPlayersError("Could not save Key Players. Please try again.");
            return;
        }

        setKeyPlayersModalOpen(false);
    };

    const copyFlysheetName = async () => {
        try {
            await navigator.clipboard.writeText(RECEIPT_FLYSHEET_NAME);
            setCopiedFlysheetName(true);
            setTimeout(() => setCopiedFlysheetName(false), 2000);
        } catch {
            setCopiedFlysheetName(false);
        }
    };

    return (
        <div className={styles.panel}>
            <div className={styles.phead}>
                <div className={`${styles.logo} ${styles.logoMap}`}>⇄</div>
                <div>
                    <div className={styles.pheadName}>Ramp ↔ CMiC mapping</div>
                    <div className={styles.pheadSub}>
                    Intercompany transactions, equipment costing, posting defaults, key players, reimbursement vendors, and receipt/invoice syncing
                    </div>
                </div>
            </div>

            <div className={styles.stepbar}>
                {STEPS.map((label, i) => {
                    const n = i + 1;
                    return (
                        <button
                            key={label}
                            type="button"
                            className={`${styles.step} ${
                                n === step
                                    ? styles.stepCur
                                    : n < step
                                    ? styles.stepDone
                                    : ""
                            }`}
                            onClick={() => onStepChange(n)}
                        >
                            <span className={styles.n}>{n}</span> {label}
                        </button>
                    );
                })}
            </div>

            {step === 1 && (
                <div className={styles.sec}>
                    <h4>Allow Intercompany Expenses</h4>
                    {intercompanyConfigured ? (
                        <p className={styles.hint}>
                            Intercompany transactions are {intercompanyEnabled ? "enabled" : "disabled"}.
                        </p>
                    ) : (
                        <>
                            <p className={styles.hint}>
                                Selecting "Yes" will create an additional "CMiC Company" custom field on your 
                                expense lines in Ramp, allowing expenses to be coded to more than one company.
                                Your team is responsible for ensuring intercompany clearing accounts are configured in CMiC and for
                                maintaining an entity-to-company mapping table in Ramp that reflects the intercompany relationships.
                                <br></br><br></br>
                                Your CMiC companies will still be mapped to native Ramp entities, and the entity on the expense will determine
                                which company is financially liable for the expense in CMiC.
                                <br></br><br></br>
                                If you are not sure whether your company will need to support intercompany transactions, it is recommended 
                                to consult with your finance team before making a selection. The integration will generally be simpler to maintain 
                                with intercompany transactions disabled.
                            </p>
                            <div className="d-flex gap-2">
                                <Button
                                    type="button"
                                    color="primary"
                                    disabled={savingIntercompany}
                                    onClick={() => saveIntercompanyChoice(true)}
                                >
                                    Yes, allow intercompany expenses
                                </Button>
                                <Button
                                    type="button"
                                    color="danger"
                                    disabled={savingIntercompany}
                                    onClick={() => saveIntercompanyChoice(false)}
                                >
                                    No, do not allow intercompany expenses
                                </Button>
                            </div>
                            {intercompanyError && (
                                <div className={styles.keyPlayersError}>
                                    {intercompanyError}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {step === 2 && (
                <div className={styles.sec}>
                    <h4>Enable Equipment Costing</h4>
                    {equipmentCostingConfigured ? (
                        <p className={styles.hint}>
                            Equipment costing is {equipmentCostingEnabled ? "enabled" : "disabled"}.
                        </p>
                    ) : (
                        <>
                            <p className={styles.hint}>
                                Will your company use equipment costing when coding expenses in Ramp, now or in the future? Selecting "Yes"
                                will create additional custom fields in Ramp to import equipment, categories and transaction codes, while
                                selecting "No" will skip their creation and not allow equipment costing to be used in Ramp. If in doubt, it generally
                                does not hurt to select "Yes". If you need to change this setting later, please reach out to Ramp support.
                            </p>
                            <div className="d-flex gap-2">
                                <Button
                                    type="button"
                                    color="primary"
                                    disabled={savingEquipmentCosting}
                                    onClick={() => saveEquipmentCostingChoice(true)}
                                >
                                    Yes, enable equipment costing
                                </Button>
                                <Button
                                    type="button"
                                    color="danger"
                                    disabled={savingEquipmentCosting}
                                    onClick={() => saveEquipmentCostingChoice(false)}
                                >
                                    No, do not enable equipment costing
                                </Button>
                            </div>
                            {equipmentCostingError && (
                                <div className={styles.keyPlayersError}>
                                    {equipmentCostingError}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {step === 3 && (
                <div className={styles.sec}>
                    <h4>CMiC posting defaults</h4>
                    <p className={styles.hint}>
                        Every transaction Dimension posts needs a journal and a
                        department. Confirm your tenant's values.
                    </p>
                    {POSTING_FIELDS.map((field, index) => (
                        <ConfigField
                            key={field.suffix}
                            field={field}
                            config={find(field.suffix)}
                            index={index}
                            onEdit={onEdit}
                        />
                    ))}
                </div>
            )}

            {step === 4 && (
                <div className={styles.sec}>
                    <h4>Key players</h4>
                    <p className={styles.hint}>
                        Enter the CMiC key player roles that should
                        map to Ramp job approvers. Saved format:
                        <code> CODE.Name|CODE2.Name2</code>.
                    </p>

                    <div className={styles.field}>
                        <div className={styles.fieldHead}>
                            <label className={styles.label}>Job approver roles</label>
                            <code className={styles.key}>{KEY_PLAYERS_SUFFIX}</code>
                        </div>
                        <p className={styles.help}>
                            Add one or more Code/Name entries, then confirm.
                            <strong> Once confirmed, this setting becomes read-only. </strong>
                            Please contact Ramp support if you need to add another role in the future.
                        </p>
                        <div className={styles.valueRow}>
                            <span className={styles.value}>
                                {keyPlayersValue || (
                                    <span className={styles.valueEmpty}>Not set</span>
                                )}
                            </span>
                            <Button
                                size="sm"
                                color={keyPlayersValue ? "secondary" : "primary"}
                                outline={!!keyPlayersValue}
                                disabled={keyPlayersLocked}
                                onClick={openKeyPlayersModal}
                            >
                                {keyPlayersLocked
                                    ? "Locked"
                                    : keyPlayersValue
                                    ? "Edit"
                                    : "Set value"}
                            </Button>
                        </div>
                        <span
                            className={`${styles.flag} ${
                                keyPlayersValue ? styles.flagSet : styles.flagOptional
                            }`}
                        >
                            {keyPlayersValue
                                ? "✓ Set (locked after confirmation)"
                                : "Optional"}
                        </span>
                    </div>

                </div>
            )}

            {step === 5 && (
                <div className={styles.sec}>
                    <h4>Reimbursement vendors</h4>
                    <p className={styles.hint}>
                        Set the default CMiC vendor code used on reimbursement
                        vouchers. This vendor must be set up in all synced
                        companies in CMiC.
                    </p>
                    {REIMBURSEMENT_VENDOR_FIELDS.map((field, index) => (
                        <ConfigField
                            key={field.suffix}
                            field={field}
                            config={find(field.suffix)}
                            index={index}
                            onEdit={onEdit}
                        />
                    ))}
                </div>
            )}

            {step === 6 && (
                <div className={styles.sec}>
                    <h4>Receipt/invoice syncing</h4>
                    <p className={styles.hint}>
                        Follow these CMiC setup steps to enable receipt
                        and invoice attachments for synced Ramp expenses/bills.
                    </p>

                    <ol className={styles.receiptSteps}>
                        <li className={styles.receiptStep}>
                            Add <code>ECM-REST-API</code> to the program security
                            bypass in the account&apos;s API security settings.
                        </li>
                        <li className={styles.receiptStep}>
                            Add your Ramp integration user to the <code>STANDARD</code> group in your imaging security.
                        </li>
                        <li className={styles.receiptStep}>
                            Create an ECM source named <code>Ramp Source</code> and
                            assign the <code>STANDARD</code> group in Source Security.
                        </li>
                        <li className={styles.receiptStep}>
                            Create an ECM document type named
                            <code> Ramp Attachments</code>, assign workflow
                            <code> CMiC Generic Workflow (07-Jun-2023)</code>
                            (ID <code>CMGENWKF</code>), and assign the
                            <code> STANDARD</code> group in Document Type Security.
                        </li>
                        <li className={styles.receiptStep}>
                            Assign <code>Ramp Source</code> as the new document
                            type&apos;s source.
                        </li>
                        <li className={styles.receiptStep}>
                            Create a flysheet with this exact name:
                            <code> {RECEIPT_FLYSHEET_NAME}</code>.
                            <Button
                                size="sm"
                                color="primary"
                                className={styles.receiptCopyButton}
                                onClick={copyFlysheetName}
                            >
                                {copiedFlysheetName
                                    ? "Copied"
                                    : "Copy to clipboard"}
                            </Button>
                        </li>
                        <li className={styles.receiptStep}>
                            On the flysheet, add these text fields (names must match
                            exactly) with max lengths of <code>100</code> and display
                            order <code>1-6</code>:
                            <ul>
                                <li>Company</li>
                                <li>Record Number</li>
                                <li>Batch Number</li>
                                <li>Amount</li>
                                <li>Remote ID</li>
                                <li>Ramp URL</li>
                            </ul>
                            You may wish to select "Company List" as the List of Values on the Company field.
                        </li>
                        <li className={styles.receiptStep}>
                            Save the flysheet, select its row, and click
                            <code> Create Structure</code> to populate the table
                            in CMiC&apos;s database.
                        </li>
                        <li className={styles.receiptStep}>
                            Assign <code>Ramp Attachments</code> as the flysheet
                            document type with display order <code>1</code>.
                        </li>
                        <li className={styles.receiptStep}>
                            Assign the <code>STANDARD</code> group in flysheet
                            security.
                        </li>
                        <li className={styles.receiptStep}>
                            On the ECM Imaging Control page, click on the <code>STANDARD</code> role under
                            Imaging Roles and enable all upload security permissions for the new <code>Ramp Attachments</code> document type.
                        </li>
                        <li className={styles.receiptStep}>
                            In <code>JC - Transaction Detail Log</code>, define a
                            new ECM search so users can open related receipt
                            images.
                            <div className={styles.receiptStepImageWrap}>
                                <a
                                    href={receiptSearchImage}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={receiptSearchImage}
                                        alt="CMiC receipt search setup example"
                                        className={styles.receiptStepImage}
                                    />
                                </a>
                            </div>
                        </li>
                        <li className={styles.receiptStep}>
                            Optionally set up the same ECM search on the General
                            Ledger Transaction Query page.
                        </li>
                        <li className={styles.receiptStep}>
                            After setup, using <code>Search ECM Documents </code>
                            on a synced transaction detail will open the matching
                            attachment when available.
                        </li>
                        <li className={styles.receiptStep}>
                            Final confirmation: once all prior steps are
                            complete, enable attachment syncing for this company.
                            <div style={{ marginTop: 8 }}>
                                <label className={styles.boolRow}>
                                    <input
                                        type="checkbox"
                                        checked={allowReceiptSync}
                                        disabled={savingReceiptSync}
                                        onChange={async (e) => {
                                            const next = e.target.checked;
                                            setSavingReceiptSync(true);
                                            await onSave(
                                                ALLOW_RECEIPT_SYNC_SUFFIX,
                                                next ? "true" : "false"
                                            );
                                            setSavingReceiptSync(false);
                                        }}
                                    />
                                    <div className={styles.boolMeta}>
                                        <span className={styles.boolLabel}>
                                            Attachment syncing confirmed for this company
                                        </span>
                                        <span className={styles.boolHelp}>
                                            Saves <code>{ALLOW_RECEIPT_SYNC_SUFFIX}</code>.
                                        </span>
                                    </div>
                                </label>
                            </div>
                        </li>
                    </ol>
                </div>
            )}

            <Modal
                isOpen={keyPlayersModalOpen}
                toggle={() => setKeyPlayersModalOpen(false)}
            >
                <ModalHeader toggle={() => setKeyPlayersModalOpen(false)}>
                    Configure Key Players
                </ModalHeader>
                <ModalBody>
                    <p className={styles.hint}>
                        Enter CMiC role code/name pairs used for Ramp job
                        approvers, exactly as they appear in CMiC. 
                        The name must not contain any vertical bars <code>|</code> or periods, 
                        and the code must only contain letters.
                    </p>
                    {keyPlayersRows.map((row, i) => (
                        <div className={styles.keyPlayersRow} key={`${i}`}> 
                            <div className={styles.keyPlayersCol}>
                                <Label className={styles.keyPlayersLabel}>Code</Label>
                                <Input
                                    value={row.code}
                                    onChange={(e) => {
                                        const next = [...keyPlayersRows];
                                        next[i] = { ...next[i], code: e.target.value };
                                        setKeyPlayersRows(next);
                                    }}
                                    placeholder="MNGR"
                                />
                            </div>
                            <div className={styles.keyPlayersCol}>
                                <Label className={styles.keyPlayersLabel}>Name</Label>
                                <Input
                                    value={row.name}
                                    onChange={(e) => {
                                        const next = [...keyPlayersRows];
                                        next[i] = { ...next[i], name: e.target.value };
                                        setKeyPlayersRows(next);
                                    }}
                                    placeholder="Project Manager"
                                />
                            </div>
                        </div>
                    ))}
                    <Button
                        color="link"
                        className={styles.keyPlayersAddBtn}
                        onClick={() =>
                            setKeyPlayersRows((prev) => [
                                ...prev,
                                { code: "", name: "" },
                            ])
                        }
                    >
                        + Add another line
                    </Button>

                    {compiledKeyPlayersPreview && (
                        <div className={styles.keyPlayersPreview}>
                            <div className={styles.keyPlayersPreviewLabel}>Preview</div>
                            <code>{compiledKeyPlayersPreview}</code>
                        </div>
                    )}

                    {keyPlayersError && (
                        <div className={styles.keyPlayersError}>{keyPlayersError}</div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        outline
                        onClick={() => setKeyPlayersModalOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        disabled={!canConfirmKeyPlayers || savingKeyPlayers}
                        onClick={confirmKeyPlayers}
                    >
                        Confirm
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
